'use client';

import type {
  KbfSelections,
  ProductInformation,
  Swatch,
} from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { toTitleCase } from '@dealerportal/utilities';
import { useEffect } from 'react';
import { Configurator, selectedIdsOf } from '../Configurator/Configurator';
import {
  DOOR_FEATURE_CODE,
  featureOptionsAsSwatches,
  findFeatureByCode,
  findFinishFeature,
  selectedOptionOf,
} from '../Configurator/featureHelpers';
import { itemCodeOf, resolveModelSrc } from '../Configurator/resolveModelSrc';
import { useItemConfiguration } from '../Configurator/useItemConfiguration';
import { useItemModel } from '../Configurator/useItemModel';
import { DownloadLink } from './DownloadLink';
import { KbfProductMedia } from './KbfProductMedia';
import type { ProductDetailsProps } from './ProductDetails';
import { ProductDetailsCtas } from './ProductDetailsCtas';
import { ProductDetailsHeader } from './ProductDetailsHeader';
import { ProductDetailsLayout } from './ProductDetailsLayout';
import { ProductDetailsRequestForm } from './ProductDetailsRequestForm';
import { ProductDetailsTabs } from './ProductDetailsTabs';
import { ProductDetailsTopBar } from './ProductDetailsTopBar';
import { ProductSpec } from './ProductSpec';
import { Section } from './Section';
import { SimilarProducts } from './SimilarProducts';
import { SwatchCollection } from './SwatchCollection';

export type KbfProductDetailsProps = ProductDetailsProps & {
  /** Back-link target; defaults to the all-products SRP. */
  backHref?: string;
  /** Back-link text; defaults to "View Products". */
  backLabel?: string;
  /**
   * Static `.glb` to fall back on when CiC cannot supply the configured
   * geometry (defaults to `resolveModelSrc(productInfo)`).
   */
  modelSrc?: string;
  /**
   * Configuration to open on, by feature and option code — what a door-style
   * SRP card links in with (`?door=330s`). See `selectionsFromSearchParams`.
   */
  initialSelections?: KbfSelections;
};

/**
 * Product detail page for kitchen, bath & fixture items (cabinets, worktops,
 * …). Shares its chrome, header, CTAs, specs, similar products and lead form
 * with the flooring page; what differs is the middle: finishes are picked as
 * swatches and the whole configuration (door style, species, hardware, …) is
 * edited in the `Configurator` next to the shared 3D viewer, whose model CiC
 * renders for the configuration as picked. No pricing: dealers do not show
 * prices for KB&F products.
 */
export const KbfProductDetails = ({
  productInfo,
  isMobileDevice,
  topSectionContent,
  marketingContent,
  actonForm,
  hideBrandColorSkuTitleInPDP,
  backHref = '/p',
  backLabel,
  modelSrc: modelSrcOverride,
  initialSelections,
}: KbfProductDetailsProps) => {
  const t = useTranslations('products');
  const ctx = useClientContext();
  const { features, isResolving, onOptionSelect } = useItemConfiguration(
    productInfo,
    { initialSelections }
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: record the visit once
  useEffect(() => {
    ctx.addRecentProduct(productInfo);
  }, []);

  const fallbackModelSrc = modelSrcOverride ?? resolveModelSrc(productInfo);
  const model = useItemModel({
    itemId: productInfo.productId,
    features,
    isResolving,
    fallbackSrc: fallbackModelSrc,
  });
  const hasConfigurator =
    features.some((feature) => feature.options.length > 0) ||
    Boolean(model.src);

  // Cabinet items rarely belong to a collection, so the item's own name is
  // the headline when the product line is blank.
  const displayInfo: ProductInformation = {
    ...productInfo,
    productName: productInfo.productName || productInfo.colorName,
  };
  const tags = (productInfo.tags ?? []).filter((tag) => tag !== 'Order_Sample');

  const finishFeature = findFinishFeature(features);
  const finishSwatches = finishFeature
    ? featureOptionsAsSwatches(finishFeature)
    : [];
  const activeFinishId = finishFeature ? selectedIdsOf(finishFeature)[0] : '';

  // The picked door style's render is what the visitor chose on the SRP, so
  // it is the hero image (the catalog's own cabinet image is a line drawing).
  const selectedDoor = selectedOptionOf(
    findFeatureByCode(features, DOOR_FEATURE_CODE)
  );

  const handleFinishSelect = (swatch: Swatch) => {
    const option = finishFeature?.options.find((o) => o.id === swatch.UPID);
    if (finishFeature && option) onOptionSelect(finishFeature, option);
  };

  const header = (
    <ProductDetailsHeader
      productInfo={displayInfo}
      isMobileDevice={isMobileDevice}
      hideBrandColorSkuTitleInPDP={hideBrandColorSkuTitleInPDP}
      tags={tags}
    />
  );

  // Unlike the flooring page, the description is not repeated up here: the
  // KB&F design shows it once, under the Overview tab below the configurator.
  const aside = (
    <>
      {finishSwatches.length > 0 && (
        <div className="pdp__swatches">
          <SwatchCollection
            swatches={finishSwatches}
            activeSwatch={activeFinishId ?? ''}
            onSelect={handleFinishSelect}
          />
        </div>
      )}

      <ProductDetailsCtas
        productInfo={productInfo}
        scrollCta={
          hasConfigurator
            ? {
                id: 'configure-button',
                label: t('configure'),
                targetId: 'configurator',
                primary: true,
              }
            : undefined
        }
        requestInfoPrimary={!hasConfigurator}
      />

      {(productInfo.files?.length ?? 0) > 0 && (
        <div>
          <DownloadLink
            files={
              productInfo.files?.map((file) => ({
                label: toTitleCase(file.type),
                file: file.uri,
              })) ?? []
            }
          />
        </div>
      )}
    </>
  );

  return (
    <ProductDetailsLayout
      isMobileDevice={isMobileDevice}
      topBar={
        <ProductDetailsTopBar
          backHref={backHref}
          backLabel={backLabel ?? t('viewProducts')}
        />
      }
      media={
        // 2D unless there is no image: the configurator shows the model.
        <KbfProductMedia
          productInfo={displayInfo}
          heroImage={selectedDoor?.imageUrl}
          model={model}
        />
      }
      header={header}
      aside={aside}
      topSectionContent={topSectionContent}
      footer={
        <ProductDetailsRequestForm>{actonForm}</ProductDetailsRequestForm>
      }
    >
      {hasConfigurator && (
        <Section
          id="configurator"
          title={t('configure')}
          description={t('configureDescription')}
          className="pdp__configurator ui-text-left"
        >
          <Configurator
            itemId={productInfo.productId}
            features={features}
            onOptionSelect={onOptionSelect}
            modelSrc={model.src}
            modelSizeBytes={model.sizeBytes}
            modelFileName={`${itemCodeOf(productInfo).toLowerCase() || 'model'}.glb`}
            modelAlt={`3D model of ${displayInfo.productName}`}
            modelLoading={model.isLoading}
            disabled={isResolving}
          />
        </Section>
      )}

      {/* Specifications only: CiC's item description is a one-line label
          ("Standard Base Cabinet") that says nothing the specs do not. */}
      {productInfo.specs?.length > 0 && (
        <ProductDetailsTabs
          className="pdp__specs"
          tabs={[
            {
              id: 'specifications',
              label: t('specifications'),
              content:
                productInfo.specs?.length > 0 ? (
                  <ProductSpec specs={productInfo.specs} />
                ) : null,
            },
          ]}
        />
      )}

      <section className="pdp__content-marketing">{marketingContent}</section>

      <SimilarProducts
        productInfo={productInfo}
        query={`type=${productInfo.productTypeSlug || productInfo.productType}`}
      />
    </ProductDetailsLayout>
  );
};
